/**
 * Reports v2 — filter/state hub. Provides the active report tab, the global
 * filters (timeframe/branch/role/user/intakes), and a real compare model. Every
 * live hook consumes these via useReportParams — the single filters→query-params
 * translation.
 *
 * The state itself lives in Redux (`reportsV2Slice`), not in `useState` here,
 * so it SURVIVES ROUTING: this provider unmounts the moment you navigate away
 * (drilling ↗ into a lead list is a navigation), which used to reset the whole
 * filter bar. Mirrors the same migration DashboardV2Context made. Per-card
 * state has its own home — see `useWidgetState`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { ReportTimeframeKey } from "../hooks/computePrevWindow";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  useKeyDimensions,
  type KeyDimension,
} from "@/features/customField/api/useKeyFields";
import { reportsV2Actions } from "./reportsV2Slice";
import { usePaymentAnalyticsEnabled } from "@/features/leads/api/useLeadFormConfig";
import { useReportParamsOverride } from "../hooks/ReportParamsOverride";

/* ------------------------------------------------------------------ tabs --- */

/** The fixed sections — the same set for every workspace of an industry. */
export type StaticReportTabKey =
  | "lead"
  | "source"
  | "country"
  | "leadUser"
  | "call"
  | "branch"
  | "admissions"
  | "team"
  | "revenue"
  | "fields";

/**
 * One "By <Team>" tab per KEY FIELD (`useKeyFields` — a dropdown an admin
 * marked "Key field"), keyed by the field's id so the key is stable across
 * loads, renames and saved views: `byKey:12`. It renders the By Country cards
 * for that field (`LeadKeyFieldReport`).
 */
export type KeyFieldTabKey = `byKey:${number}`;

export type ReportTabKey = StaticReportTabKey | KeyFieldTabKey;

export interface ReportTabDef {
  key: ReportTabKey;
  label: string;
  /** Key-field tabs only — the field the tab counts by. */
  dimension?: KeyDimension;
}

export const keyFieldTabKey = (fieldId: number): KeyFieldTabKey =>
  `byKey:${fieldId}`;

/** `"byKey:12"` → 12; null for every fixed tab (and anything malformed). */
export function keyFieldIdOfTab(tab: string): number | null {
  const m = /^byKey:(\d+)$/.exec(tab);
  return m ? Number(m[1]) : null;
}

export const isKeyFieldTabKey = (tab: string): tab is KeyFieldTabKey =>
  keyFieldIdOfTab(tab) != null;

/**
 * Top-level report sections. By Source / By Country / User Performance used
 * to be SUB-tabs inside Lead Overview — one click deeper than the sections they
 * sit beside, and invisible until you opened Lead Overview. They are peers of
 * it, so they live here. `leadUser` is keyed apart from the `team` (Team) tab:
 * different reports that happen to be about the same noun. (By Stage —
 * Counsellor × Stage, Stage Velocity & Aging, Tracks — was removed entirely;
 * owner, 2026-10-20. A saved view or stored tab still naming `leadStage` falls
 * back to Lead Overview via the visible-tabs check below.) The per-tenant
 * "By <Team>" key-field tabs are not listed here — `useVisibleReportTabs`
 * adds them from the workspace's key fields.
 */
export const REPORT_TABS: ReportTabDef[] = [
  { key: "lead", label: "Lead Overview" },
  { key: "source", label: "By Source" },
  { key: "country", label: "By Country" },
  { key: "leadUser", label: "User Performance" },
  { key: "call", label: "Call Analytics" },
  { key: "branch", label: "Branch Analytics" },
  { key: "admissions", label: "Admissions" },
  { key: "team", label: "Team" },
  { key: "revenue", label: "Revenue" },
  { key: "fields", label: "Field Insights" },
];

/** A tab key this build knows — fixed or key field. A saved view naming one
 *  is restorable; whether THIS tenant may see it is still decided by
 *  `useVisibleReportTabs` (an unknown or hidden key lands on Lead Overview). */
export function isReportTabKey(tab: string): tab is ReportTabKey {
  return REPORT_TABS.some((t) => t.key === tab) || isKeyFieldTabKey(tab);
}

/** Same education/industry detection the intake/university widgets and the
 *  import flow use: tenant.industryId === 1 (Education). */
export const EDUCATION_INDUSTRY_ID = 1;

export function useIsEducationTenant(): boolean {
  const industryId = useAppSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.auth?.currentUser?.user?.tenant?.industryId
  );
  return industryId === EDUCATION_INDUSTRY_ID;
}

/**
 * Which sections a tenant sees is INDUSTRY-conditional in both directions —
 * the tab bar is not a superset that study-abroad happens to see all of.
 */

/** Hidden FOR study-abroad (industryId 1). Admissions/Team are not the read
 *  those workspaces want. (Revenue used to be on this list too; it now follows
 *  the tenant's "Payment analytics" setting — see `buildVisibleReportTabs`.) */
const HIDDEN_FOR_STUDY_ABROAD: ReportTabKey[] = ["admissions", "team"];

/** Study-abroad ONLY — country/destination reporting is a study-abroad concept
 *  and the tab must not exist elsewhere (its cards already self-hide on the
 *  server's `enabled === false`, but an empty tab is still a dead tab). */
const STUDY_ABROAD_ONLY: ReportTabKey[] = ["country"];

/**
 * The tab bar for one tenant. Key-field tabs are NOT industry-gated (a key
 * field is admin-chosen, so it means something in any industry): one per
 * field, in By Country's slot — right after it where it shows, right after
 * By Source where it doesn't. Pure, so the order is unit-tested.
 */
export function buildVisibleReportTabs(
  isStudyAbroad: boolean,
  keyDimensions: KeyDimension[],
  /** "Payment analytics" (Settings → Manage) — the Revenue tab shows only
   *  when it is on. Defaults to the rule before the setting existed (off for
   *  study abroad), which is also the setting's own default. */
  paymentAnalytics: boolean = !isStudyAbroad
): ReportTabDef[] {
  const hidden: ReportTabKey[] = [
    ...(isStudyAbroad ? HIDDEN_FOR_STUDY_ABROAD : STUDY_ABROAD_ONLY),
    ...(paymentAnalytics ? [] : (["revenue"] as ReportTabKey[])),
  ];
  const fixed = REPORT_TABS.filter((t) => !hidden.includes(t.key));
  const keyTabs: ReportTabDef[] = keyDimensions
    .filter((d) => d.kind === "key" && d.fieldId != null)
    .map((d) => ({
      key: keyFieldTabKey(d.fieldId as number),
      label: `By ${d.singular}`,
      dimension: d,
    }));
  if (keyTabs.length === 0) return fixed;
  const at = fixed.findIndex(
    (t) => t.key === (isStudyAbroad ? "country" : "source")
  );
  return [...fixed.slice(0, at + 1), ...keyTabs, ...fixed.slice(at + 1)];
}

/** The tabs THIS tenant may see, plus whether the key fields behind the
 *  "By <Team>" tabs are still loading. */
function useReportTabsState(): { tabs: ReportTabDef[]; keyFieldsLoading: boolean } {
  const isStudyAbroad = useIsEducationTenant();
  const paymentAnalytics = usePaymentAnalyticsEnabled();
  const { dimensions, isLoading: keyFieldsLoading } = useKeyDimensions();
  const tabs = useMemo(
    () => buildVisibleReportTabs(isStudyAbroad, dimensions, paymentAnalytics),
    [isStudyAbroad, dimensions, paymentAnalytics]
  );
  return { tabs, keyFieldsLoading };
}

/** The tabs THIS tenant may see — tab bar AND body both consume this, so a tab
 *  filtered out here can never be reached, however it was selected. */
export function useVisibleReportTabs(): ReportTabDef[] {
  return useReportTabsState().tabs;
}

/**
 * What was SELECTED vs what is REACHABLE (see `useReportsV2`). A key-field
 * tab selected before the custom fields have loaded (a restored view, a
 * return from a drill) is held rather than bounced: its body shows a loading
 * state instead of flashing Lead Overview and firing that tab's whole burst.
 */
export function resolveActiveTab(
  requested: ReportTabKey,
  visibleTabs: ReportTabDef[],
  keyFieldsLoading: boolean
): ReportTabKey {
  if (visibleTabs.some((t) => t.key === requested)) return requested;
  if (keyFieldsLoading && isKeyFieldTabKey(requested)) return requested;
  return "lead";
}

/* ------------------------------------------------------- timeframe presets -- */

export interface TimeframeOption {
  key: ReportTimeframeKey;
  label: string;
  /** dropdown section header (rendered above the first item of each group) */
  group?: string;
}

/**
 * Reports timeframe presets — the full enterprise set (matches LeadSquared /
 * Zoho / Salesforce): day, week, month, rolling N-day, quarter, year, all-time,
 * custom. Grouped for scannability; every key resolves in resolveReportWindow.
 */
export const REPORT_TIMEFRAME_OPTIONS: TimeframeOption[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week", group: "Week" },
  { key: "last_week", label: "Last week", group: "Week" },
  { key: "this_month", label: "This month", group: "Month" },
  { key: "last_month", label: "Last month", group: "Month" },
  { key: "last_7_days", label: "Last 7 days", group: "Rolling" },
  { key: "last_30_days", label: "Last 30 days", group: "Rolling" },
  { key: "last_90_days", label: "Last 90 days", group: "Rolling" },
  { key: "this_quarter", label: "This quarter", group: "Quarter" },
  { key: "last_quarter", label: "Last quarter", group: "Quarter" },
  { key: "this_year", label: "This year", group: "Year" },
  { key: "last_year", label: "Last year", group: "Year" },
  { key: "all", label: "All time", group: "Other" },
  { key: "custom", label: "Custom range", group: "Other" },
];

const PERIOD_NOUN: Record<ReportTimeframeKey, string> = {
  all: "all time",
  today: "today",
  yesterday: "yesterday",
  this_week: "this week",
  last_week: "last week",
  this_month: "this month",
  last_month: "last month",
  last_7_days: "the last 7 days",
  last_30_days: "the last 30 days",
  last_90_days: "the last 90 days",
  this_quarter: "this quarter",
  last_quarter: "last quarter",
  this_year: "this year",
  last_year: "last year",
  custom: "this range",
};
export const periodNoun = (t: ReportTimeframeKey): string =>
  PERIOD_NOUN[t] ?? "this period";

/* ----------------------------------------------------------- compare model -- */

export type CompareType = "" | "previous" | "custom";

/** Mirrors v1 RequestCompare but with a real, consumable model. */
export interface RequestCompare {
  /** "" = off, "previous" = previous period, "custom" = explicit date range */
  type: CompareType;
  startDate?: string;
  endDate?: string;
  isCompare: boolean;
}

/* --------------------------------------------------------- context shape ---- */

interface ReportsV2Value {
  // active report tab
  activeTab: ReportTabKey;
  setActiveTab: (t: ReportTabKey) => void;

  // global report filters (become query params via useReportParams)
  timeframe: ReportTimeframeKey;
  setTimeframe: (t: ReportTimeframeKey) => void;
  branch: string;
  setBranch: (b: string) => void;
  role: string;
  setRole: (r: string) => void;
  user: string;
  /** Picking a user pins their role when the caller passes the user's roleId
   *  (ReportFilterBar reads it off the live filter-options payload). */
  setUser: (u: string, roleId?: string | null) => void;
  customStart: string | null;
  customEnd: string | null;
  setCustomRange: (start: string | null, end: string | null) => void;

  /**
   * Selected intakes as `"<year>-<month0>"` keys (month 0-indexed), the SAME
   * key model the dashboard's Intake picker uses — `[]` = no filter (all
   * intakes). Study-abroad only; the pill hides when the tenant has no Intake
   * custom fields. Serialized to `?intakeKeys=` by useReportParams.
   */
  intakes: string[];
  setIntakes: (keys: string[]) => void;

  /**
   * Global custom-field pills: `{ [customInputId]: optionId }` for SELECTOR
   * fields marked "Use as global filter". Serialized to `cf_<id>` params by
   * useReportParams — the same params the Leads page sends.
   */
  globalCf: Record<string, string>;
  setGlobalCf: (fieldId: string, optionId: string | null) => void;

  // compare (render flag + period model)
  compare: RequestCompare;
  setCompare: (c: RequestCompare) => void;
  toggleCompare: () => void;

  // reset everything to defaults
  reset: () => void;
}

/**
 * Holds no state any more — but it is NOT decoration. `useReportsV2` asserts on
 * it, so a report card mounted somewhere that isn't the report page (a card
 * embedded in the dashboard, a scheduled-report preview) still fails loudly
 * instead of silently sharing and cross-driving the live filter bar. The state
 * itself lives in Redux (`reportsV2Slice`), so it survives this component
 * unmounting on navigation.
 */
const MountedCtx = createContext(false);

export function ReportsV2Provider({ children }: { children: ReactNode }) {
  return <MountedCtx.Provider value={true}>{children}</MountedCtx.Provider>;
}

/* ------------------------------------------------- field-scoped selectors --- */
/**
 * Read ONE filter field. Prefer these over `useReportsV2()` anywhere you don't
 * need the whole bar: `useReportsV2` subscribes to all nine fields, so a card
 * that only wants the window label used to re-render every time anyone touched
 * `compare` or `intakes` — and `useWindowLabel`/`useScopeLabel` are called by
 * ~39 cards, which made that the widest re-render channel on the page.
 */
// A card mounted on another surface (the dashboard) reads that surface's pick
// through `ReportParamsOverride` — its header labels then name the window and
// scope its numbers ran on, not the Reports page's bar. null on the Reports
// page, so these read the bar exactly as before.
export const useReportTimeframe = (): ReportTimeframeKey => {
  const override = useReportParamsOverride();
  const timeframe = useAppSelector((s) => s.reportsV2.timeframe);
  return override?.timeframe ?? timeframe;
};
export const useReportBranch = (): string => {
  const override = useReportParamsOverride();
  const branch = useAppSelector((s) => s.reportsV2.branch);
  return override?.scope ? (override.scope.branch ?? "all") : branch;
};
export const useReportRole = (): string => {
  const override = useReportParamsOverride();
  const role = useAppSelector((s) => s.reportsV2.role);
  return override?.scope ? (override.scope.role ?? "all") : role;
};
export const useReportUser = (): string => {
  const override = useReportParamsOverride();
  const user = useAppSelector((s) => s.reportsV2.user);
  return override?.scope ? (override.scope.user ?? "all") : user;
};
export const useReportIntakes = (): string[] =>
  useAppSelector((s) => s.reportsV2.intakes);
export const useReportGlobalCf = (): Record<string, string> =>
  useAppSelector((s) => s.reportsV2.globalCf);
export const useReportCompare = (): RequestCompare =>
  useAppSelector((s) => s.reportsV2.compare);
/** Just the on/off flag — the common case, and a primitive, so no identity churn. */
export const useReportIsCompare = (): boolean =>
  useAppSelector((s) => s.reportsV2.compare.isCompare);

/** The active tab, with the same industry/dead-key fallback `useReportsV2` applies. */
export function useReportActiveTab(): ReportTabKey {
  const requested = useAppSelector((s) => s.reportsV2.activeTab);
  const { tabs, keyFieldsLoading } = useReportTabsState();
  return resolveActiveTab(requested, tabs, keyFieldsLoading);
}

export function useReportsV2(): ReportsV2Value {
  const dispatch = useAppDispatch();
  const mounted = useContext(MountedCtx);
  if (!mounted) {
    throw new Error("useReportsV2 must be used within <ReportsV2Provider>");
  }
  const requestedTab = useAppSelector((s) => s.reportsV2.activeTab);
  const timeframe = useAppSelector((s) => s.reportsV2.timeframe);
  const branch = useAppSelector((s) => s.reportsV2.branch);
  const role = useAppSelector((s) => s.reportsV2.role);
  const user = useAppSelector((s) => s.reportsV2.user);
  const customStart = useAppSelector((s) => s.reportsV2.customStart);
  const customEnd = useAppSelector((s) => s.reportsV2.customEnd);
  const intakes = useAppSelector((s) => s.reportsV2.intakes);
  const globalCf = useAppSelector((s) => s.reportsV2.globalCf);
  const compare = useAppSelector((s) => s.reportsV2.compare);

  // A saved view (ViewsMenu) stores a tabKey and can outlive the tenant's
  // industry gate, so what was SELECTED and what is REACHABLE are two things.
  // Derived — never a setState-in-effect — so an industry-hidden tab silently
  // falls back to Lead Overview instead of rendering a tab with no tab button.
  // This ALSO covers a tab key that was removed from the build entirely (the
  // store can be holding one from before a deploy, or a saved view can name it).
  // A key-field tab whose field was un-ticked (or deleted) falls back the same way.
  const { tabs: visibleTabs, keyFieldsLoading } = useReportTabsState();
  const activeTab = resolveActiveTab(requestedTab, visibleTabs, keyFieldsLoading);

  const setActiveTab = useCallback(
    (t: ReportTabKey) => {
      dispatch(reportsV2Actions.setActiveTab(t));
    },
    [dispatch]
  );
  const setTimeframe = useCallback(
    (t: ReportTimeframeKey) => {
      dispatch(reportsV2Actions.setTimeframe(t));
    },
    [dispatch]
  );
  const setBranch = useCallback(
    (b: string) => {
      dispatch(reportsV2Actions.setBranch(b));
    },
    [dispatch]
  );
  const setRole = useCallback(
    (r: string) => {
      dispatch(reportsV2Actions.setRole(r));
    },
    [dispatch]
  );
  // Dependent rule: picking a specific user pins (and implies) their role.
  // The role id comes from the LIVE filter-options payload via the caller —
  // the old mock USER_OPTIONS lookup silently no-oped on real numeric ids.
  const setUser = useCallback(
    (u: string, roleId?: string | null) => {
      dispatch(reportsV2Actions.setUser({ user: u, roleId }));
    },
    [dispatch]
  );
  const setCustomRange = useCallback(
    (start: string | null, end: string | null) => {
      dispatch(reportsV2Actions.setCustomRange({ start, end }));
    },
    [dispatch]
  );
  const setIntakes = useCallback(
    (keys: string[]) => {
      dispatch(reportsV2Actions.setIntakes(keys));
    },
    [dispatch]
  );
  const setGlobalCf = useCallback(
    (fieldId: string, optionId: string | null) => {
      dispatch(reportsV2Actions.setGlobalCf({ fieldId, optionId }));
    },
    [dispatch]
  );
  const setCompare = useCallback(
    (c: RequestCompare) => {
      dispatch(reportsV2Actions.setCompare(c));
    },
    [dispatch]
  );
  const toggleCompare = useCallback(() => {
    dispatch(reportsV2Actions.toggleCompare());
  }, [dispatch]);
  const reset = useCallback(() => {
    dispatch(reportsV2Actions.reset());
  }, [dispatch]);

  return useMemo<ReportsV2Value>(
    () => ({
      activeTab,
      setActiveTab,
      timeframe,
      setTimeframe,
      branch,
      setBranch,
      role,
      setRole,
      user,
      setUser,
      customStart,
      customEnd,
      setCustomRange,
      intakes,
      setIntakes,
      globalCf,
      setGlobalCf,
      compare,
      setCompare,
      toggleCompare,
      reset,
    }),
    [
      activeTab,
      setActiveTab,
      timeframe,
      setTimeframe,
      branch,
      setBranch,
      role,
      setRole,
      user,
      setUser,
      customStart,
      customEnd,
      setCustomRange,
      intakes,
      setIntakes,
      globalCf,
      setGlobalCf,
      compare,
      setCompare,
      toggleCompare,
      reset,
    ]
  );
}

/* ------------------------------------------------- export snapshot bridge --- */
/**
 * The Export button lives in the white top app bar (ReportHeaderTitle via
 * Header.tsx) — OUTSIDE <ReportsV2Provider>. This tiny external store bridges
 * the gap: ReportsV2 (inside the provider) publishes the active tab + the
 * CURRENT query params (the same useReportParams output every report hook
 * sends), and the header button subscribes via useSyncExternalStore. A null
 * snapshot means the v2 report page isn't mounted (v1 toggle) — the button
 * then explains itself instead of exporting the wrong thing.
 */
export interface ReportExportSnapshot {
  activeTab: ReportTabKey;
  /** current-window query params — EXACTLY what the tab's hooks are sending
   *  (useReportParams output: startDate/endDate/branchId/roleId/userId/intakeKeys) */
  params: {
    startDate?: string;
    endDate?: string;
    branchId?: string;
    roleId?: string;
    userId?: string;
    /** selected intakes, comma-joined `"<year>-<month0>"` keys */
    intakeKeys?: string;
    /** global custom-field pills — one `cf_<customInputId>=<optionId>` each */
    [cfKey: `cf_${string}`]: string | undefined;
  };
}

let exportSnapshot: ReportExportSnapshot | null = null;
const exportListeners = new Set<() => void>();

export function publishReportExportSnapshot(
  snapshot: ReportExportSnapshot | null
): void {
  exportSnapshot = snapshot;
  exportListeners.forEach((l) => l());
}

function subscribeExportSnapshot(listener: () => void): () => void {
  exportListeners.add(listener);
  return () => exportListeners.delete(listener);
}

const getExportSnapshot = () => exportSnapshot;

export function useReportExportSnapshot(): ReportExportSnapshot | null {
  return useSyncExternalStore(subscribeExportSnapshot, getExportSnapshot);
}
